import { ArpMediaType } from '@arpeggi/shared'
import { observer } from 'mobx-react-lite'
import { Dispatch, FC, ReactElement, SetStateAction, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { IArpMedia } from '../../../../features/arp-registry/ArpMedia'
import ArpRegistry from '../../../../features/arp-registry/ArpRegistry'
import { EmptyState } from '../../../../features/empty-state'
import { getArpMedia } from '../../../api/v2/ArpMediaAPI'
import { styled } from '../../../lib/stitches'
import { useTreeState } from '../../../stores/Root'
import Button from '../../core/Button/Button'
import CenteredLoadingAnimation from '../../core/CenteredLoadingAnimation'
import { Column, Row, StyledScrollable } from '../../core/Components'
import { RepeatIcon, ShuffleIcon } from '../../core/icons'
import { Soundwave } from '../../core/icons/responsive-icons'
import { useAsyncEffect, useRemix } from '../explore/common/utils'
import { BabyTableRow, DetailsButton } from '../new-explore/components/TableRow'
import Tabs from '../new-explore/components/Tabs'
import useSelectedMedia from '../new-explore/hooks/useSelectedMedia'
import MediaTitleSection from './MediaTitleSection'

const Tab = {
  SAMPLE: 'samples',
  LOOP: 'loop',
  REMIX: 'remixes'
}

const Container = styled(Column, {
  height: '100%',
  width: '100%',
  backgroundColor: '$gray005',
  clipPath: 'inset(-100% 0px 0px -100%)',
  variants: {
    isExpanded: {
      false: {
        height: '0px'
      }
    }
  }
})

const useMediaDNABody = (media: IArpMedia | undefined) => {
  const [tab, setTab] = useState<string | undefined>(undefined)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [remixes, setRemixes] = useState<IArpMedia[]>([])
  const [samples, setSamples] = useState<IArpMedia[]>([])
  const [loops, setLoops] = useState<IArpMedia[]>([])

  const type = media.type

  useAsyncEffect(async () => {
    if (!media) return

    setRemixes([])
    setSamples([])
    setLoops([])

    setIsLoading(true)

    if (type === ArpMediaType.SONG) {
      setTab(Tab.SAMPLE)

      const layerOne = media.subComponents
      const layerOneArps = await Promise.all(layerOne.map((id) => ArpRegistry.getByIdAsync(id)))
      const layerTwo = layerOneArps.flatMap((arp) => arp.subComponents)
      const layerTwoArps = await Promise.all(layerTwo.map((id) => ArpRegistry.getByIdAsync(id)))
      const subcomponents = [...layerOneArps, ...layerTwoArps]
      setSamples(subcomponents.filter((arp) => arp?.type === ArpMediaType.SAMPLE))
      setLoops(subcomponents.filter((arp) => arp?.type === ArpMediaType.STEM))
    } else {
      setTab(Tab.REMIX)

      // TODO this could max out at 50 if lots of remixes, should paginate
      const remixes = await getArpMedia({
        subComponentId: [media.id],
        shadowBlocklist: true,
        type: [ArpMediaType.SONG]
      })

      const newRemixes = await Promise.all(
        remixes.data.map((item) => ArpRegistry.getByIdAsync(item.id))
      )
      const songRemixes = newRemixes.filter((item) => item.type === ArpMediaType.SONG)
      setRemixes(songRemixes)
    }

    setIsLoading(false)
  }, [media])

  let data: IArpMedia[] = []
  switch (tab) {
    case Tab.SAMPLE:
      data = samples
      break
    case Tab.LOOP:
      data = loops
      break
    case Tab.REMIX:
      data = remixes
      break
  }

  return { media, isLoading, data, tab, setTab }
}

interface MediaDNARowsProps {
  data: IArpMedia[]
  isLoading: boolean
  emptyState: ReactElement
}

const MediaDNARows: FC<MediaDNARowsProps> = observer(({ data, isLoading, emptyState }) => {
  const { globalPlayer } = useTreeState()
  const { handleSelect } = useSelectedMedia()

  const onClick = async (arp: IArpMedia) => {
    globalPlayer.setDnaIsLocked(true)
    if (arp.id === globalPlayer.arpMediaIndex) {
      await globalPlayer.play()
    } else {
      await handleSelect({ id: arp.id })
    }
    globalPlayer.setDnaIsLocked(false)
  }

  const height = '352px'
  if (isLoading)
    return (
      <Column css={{ height }} verticalCenter horizontalCenter>
        <CenteredLoadingAnimation small />
      </Column>
    )
  if (data.length === 0) {
    return emptyState
  }
  return (
    <StyledScrollable css={{ height }}>
      {data.map((arp) => (
        <BabyTableRow
          media={arp}
          onClick={() => onClick(arp)}
          active={arp.id === globalPlayer.arpMediaIndex}
        />
      ))}
    </StyledScrollable>
  )
})

const MediaDNAHeader: FC<{ media: IArpMedia }> = ({ media }) => {
  const navigate = useNavigate()
  if (!media) return null

  const goToDetailsPage = () => navigate(media.detailPageUrl)

  return (
    <Column css={{ padding: '20px 0px' }}>
      <Row verticalCenter css={{ marginRight: '15px' }}>
        <MediaTitleSection mediaOverride={media} />
        <DetailsButton media={media} />
      </Row>
      <Button
        onClick={goToDetailsPage}
        css={{ marginTop: '15px', marginLeft: '15px', marginRight: '15px', height: '32px' }}
        type="primary"
        rounded
      >
        {`View ${media.type === ArpMediaType.SONG ? 'Song' : 'Sound'} Details`}
      </Button>
    </Column>
  )
}

const SoundMediaDNATabs: FC<{ tab: string; setTab: Dispatch<SetStateAction<string>> }> = ({
  tab,
  setTab
}) => (
  <Tabs.Root value={tab} onValueChange={(newVal) => setTab(newVal)}>
    <Tabs.List css={{ width: '100%' }}>
      <Tabs.Trigger value={Tab.REMIX} dark weight="medium" font="dm">
        <ShuffleIcon style={{ width: '16px', height: '16px', marginRight: '5px' }} />
        Remixes
      </Tabs.Trigger>
    </Tabs.List>
  </Tabs.Root>
)

const SongMediaDNATabs: FC<{ tab: string; setTab: Dispatch<SetStateAction<string>> }> = ({
  tab,
  setTab
}) => (
  <Tabs.Root value={tab} onValueChange={(newVal) => setTab(newVal)}>
    <Tabs.List css={{ width: '100%' }}>
      <Tabs.Trigger value={Tab.SAMPLE} dark weight="medium" font="dm">
        <Soundwave style={{ width: '16px', height: '16px', marginRight: '5px' }} />
        Samples
      </Tabs.Trigger>
      <Tabs.Trigger value={Tab.LOOP} dark weight="medium" font="dm">
        <RepeatIcon style={{ width: '16px', height: '16px', marginRight: '5px' }} />
        Stems
      </Tabs.Trigger>
    </Tabs.List>
  </Tabs.Root>
)

interface Props {
  isExpanded: boolean
  media: IArpMedia
}

const MediaDNABody: FC<Props> = ({ isExpanded, media }) => {
  const state = useMediaDNABody(media)
  const { remixSample, remixStem, remixSong } = useRemix()
  const shouldUseSongTabs = state.media?.type === ArpMediaType.SONG
  const shouldUseSoundTabs =
    state.media?.type === ArpMediaType.STEM || state.media?.type === ArpMediaType.SAMPLE

  let emptyStateText: string = ''
  let primaryButtonText: string | undefined = undefined
  let primaryButtonOnClick: () => void | undefined = undefined
  switch (state.tab) {
    case Tab.SAMPLE:
      emptyStateText = 'No samples available'
      break
    case Tab.LOOP:
      emptyStateText = 'No stems available'
      break
    case Tab.REMIX:
      emptyStateText = 'Be the first to remix this song'
      primaryButtonText = 'Use in studio'
      primaryButtonOnClick = () => {
        if (media.type === ArpMediaType.SONG) return remixSong(media.id)
        if (media.type === ArpMediaType.STEM) return remixStem(media.id)
        else return remixSample(media.id)
      }
      break
  }

  return (
    <Container isExpanded={isExpanded}>
      <MediaDNAHeader media={media} />
      {shouldUseSongTabs && <SongMediaDNATabs tab={state.tab} setTab={state.setTab} />}
      {shouldUseSoundTabs && <SoundMediaDNATabs tab={state.tab} setTab={state.setTab} />}
      <MediaDNARows
        data={state.data}
        isLoading={state.isLoading}
        emptyState={
          <EmptyState.Root>
            <EmptyState.Icon />
            <EmptyState.Text muted={primaryButtonText === undefined}>
              {emptyStateText}
            </EmptyState.Text>
            {primaryButtonText && (
              <EmptyState.ButtonRow>
                <EmptyState.PrimaryButton onClick={primaryButtonOnClick}>
                  {primaryButtonText}
                </EmptyState.PrimaryButton>
              </EmptyState.ButtonRow>
            )}
          </EmptyState.Root>
        }
      />
    </Container>
  )
}

export default observer(MediaDNABody)
